import { Badge } from "ui/badge";
import ConfirmDialog from "ui/ConfirmDialog";
import { getApiErrorMessage } from "utils/apiErrorMessage";
import {
  createQardanPolicy,
  deleteQardanPolicy,
  updateQardanPolicy,
  getQardanPolicyList,
  getQarzanListTypes,
} from "services/QardanpolicyServices";
import { getEmployeeTypes, getGrades, getEmploymentStatuses } from "services/leavePolicyService";
import { Switch } from "../../ui/switch";
import { Label } from "../../ui/label";
import { FullScreenLoading } from "components/ui/loader";
import useDebounce from "hooks/useDebounce";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Edit3,
  FileText,
  Plus,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { toast } from "sonner";
import Pagination from "../../ui/pagination";
import { InputFieldset } from "../../ui/inputFieldset";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";

type Row = Record<string, string | number | boolean> & {
  id: string | number;
  status?: string;
};

type FormField = {
  label: string;
  key: string;
  type: "text" | "number" | "select";
};

const policyFields: FormField[] = [
  { label: "Policy Name", key: "policyName", type: "text" },
  {
    label: "Qardan Type",
    key: "qarzanTypePublicId",
    type: "select",
  },
  {
    label: "Employee Type",
    key: "employeeTypePublicId",
    type: "select",
  },
  {
    label: "Employment Status",
    key: "employmentStatusPublicId",
    type: "select",
  },
  {
    label: "Grade",
    key: "gradePublicId",
    type: "select",
  },
  { label: "Min Service (Months)", key: "service", type: "number" },
  { label: "Max Active Qardans", key: "maximum", type: "number" },
];

export function QardanPolicyPage() {
  const { setPageHeader } = usePageHeader();
  const [rows, setRows] = useState<Row[]>([]);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("type");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const filters = useMemo(
    () => ({ policyName: search, pageNo: currentPage, pageSize }),
    [search, currentPage, pageSize],
  );
  const debouncedFilters = useDebounce(filters, 400);

  useEffect(() => {
    const controller = new AbortController();
    const fetchPolicies = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const page = await getQardanPolicyList(
          debouncedFilters,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setRows(
          page.data.map((item) => ({
            id: item.publicId,
            policyName: item.policyName,
            qarzanTypePublicId: item.qarzanTypePublicId ?? "",
            employeeTypePublicId: item.employeeTypePublicId ?? "",
            employmentStatusPublicId: item.employmentStatusPublicId ?? "",
            gradePublicId: item.gradePublicId ?? "",
            type: item.qarzanTypeName ?? item.policyName ?? "—",
            employee: item.employeeTypeName ?? "—",
            employment: item.employmentStatusName ?? "—",
            grade: item.gradeName ?? "—",
            service: item.minService ?? "—",
            maximum: item.maxActiveQarzan ?? "—",
            status: item.isActive ? "Active" : "Inactive",
          })),
        );
        setTotalRecords(page.totalRecords);
      } catch (error) {
        if (controller.signal.aborted) return;
        setRows([]);
        setTotalRecords(0);
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load qardan policies.";
        setLoadError(message);
        toast.error(message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchPolicies();
    return () => controller.abort();
  }, [debouncedFilters, retryCount]);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<Row | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Row | null>(null);
  const [form, setForm] = useState<Row>({ id: 0, status: "Active" });
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, { value: string; label: string }[]>>({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionErrors, setOptionErrors] = useState<Record<string, string>>({});
  const [optionsRetry, setOptionsRetry] = useState(0);
  const dialogOpen = !!editing;

  useEffect(() => {
    setPageHeader({
      title: "Qardan Policy",
      icon: <Users className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    if (!dialogOpen) return;
    let cancelled = false;
    setOptionsLoading(true);
    setOptionErrors({});
    Promise.allSettled([getEmployeeTypes(), getGrades(), getEmploymentStatuses(), getQarzanListTypes()])
      .then(([types, grades, statuses, qarzanTypes]) => {
        if (cancelled) return;
        const options: Record<string, { value: string; label: string }[]> = {};
        const errors: Record<string, string> = {};
        if (types.status === "fulfilled") {
          options.employeeTypePublicId = types.value.map((item) => ({ value: item.publicId, label: item.typeName }));
        } else errors.employeeTypePublicId = "Unable to load employee types.";
        if (grades.status === "fulfilled") {
          options.gradePublicId = grades.value.map((item) => ({ value: item.publicId, label: item.gradeName }));
        } else errors.gradePublicId = "Unable to load grades.";
        if (statuses.status === "fulfilled") {
          options.employmentStatusPublicId = statuses.value.map((item) => ({ value: item.publicId, label: item.statusName }));
        } else errors.employmentStatusPublicId = "Unable to load employment statuses.";
        if (qarzanTypes.status === "fulfilled" && Array.isArray(qarzanTypes.value)) {
          options.qarzanTypePublicId = qarzanTypes.value
            .filter((item) => item.isActive && item.publicId != null)
            .map((item) => ({ value: String(item.publicId), label: item.qarzanTypeName }));
        } else errors.qarzanTypePublicId = "Unable to load qardan types.";
        setDropdownOptions(options);
        setOptionErrors(errors);
        setOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [dialogOpen, optionsRetry]);

  const getFieldOptions = (field: FormField) => {
    const options = dropdownOptions[field.key] ?? [];
    const selected = String(form[field.key] ?? "");
    const nameKey = field.key === "qarzanTypePublicId" ? "type" : field.key === "employeeTypePublicId" ? "employee" : field.key === "gradePublicId" ? "grade" : "employment";
    if (editing && selected && !options.some((option) => option.value === selected)) {
      return [{ value: selected, label: String(editing[nameKey] ?? selected) }, ...options];
    }
    return options;
  };

  const pageRows = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          String(a[sortKey] ?? "").localeCompare(
            String(b[sortKey] ?? ""),
            undefined,
            { numeric: true },
          ) * (sortDirection === "asc" ? 1 : -1),
      ),
    [rows, sortKey, sortDirection],
  );


  const openForm = (row?: Row) => {
    const value = row ? { ...row } : { id: Date.now(), status: "Active" };
    policyFields.forEach(({ key }) => {
      if (value[key] === undefined) value[key] = "";
    });
    setFormErrors({});
    setForm(value);
    setEditing(row || value);
  };

  const updateField = (key: string, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormErrors((current) => ({ ...current, [key]: "" }));
  };


  const save = async () => {
    if (saving) return;
    const errors: Record<string, string> = {};
    policyFields.forEach((field) => {
      const value = String(form[field.key] ?? "").trim();
      if (!value) {
        errors[field.key] = `${field.label} is required.`;
      } else if (
        field.type === "number" &&
        (!Number.isSafeInteger(Number(value)) || Number(value) < 0)
      ) {
        errors[field.key] =
          `${field.label} must be a whole number of zero or greater.`;
      }
    });
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const isUpdating = typeof form.id === "string";
    setSaving(true);
    try {
      const payload = {
        policyName: String(form.policyName).trim(),
        qarzanTypePublicId: String(form.qarzanTypePublicId),
        employeeTypePublicId: String(form.employeeTypePublicId),
        employmentStatusPublicId: String(form.employmentStatusPublicId),
        gradePublicId: String(form.gradePublicId),
        minService: Number(form.service),
        maxActiveQarzan: Number(form.maximum),
        isActive: form.status === "Active",
      };
      if (isUpdating) {
        await updateQardanPolicy(String(form.id), payload);
      } else {
        await createQardanPolicy(payload);
      }
      toast.success(`Qardan policy ${isUpdating ? "updated" : "created"} successfully.`);
      setEditing(null);
      setRetryCount((count) => count + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to ${isUpdating ? "update" : "create"} qardan policy.`));
    } finally {
      setSaving(false);
    }
  };


  const confirmDelete = async () => {
    if (!policyToDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteQardanPolicy(String(policyToDelete.id));
      toast.success("Qardan policy deleted successfully.");
      setPolicyToDelete(null);
      const lastPage = Math.max(1, Math.ceil((totalRecords - 1) / pageSize));
      if (currentPage > lastPage) setCurrentPage(lastPage);
      else setRetryCount((count) => count + 1);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete qardan policy."));
    } finally {
      setDeleting(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {loadError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          <span>{loadError}</span>
          <Button
            variant="outline"
            onClick={() => setRetryCount((count) => count + 1)}
          >
            Retry
          </Button>
        </div>
      )}
      <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-wrap items-center gap-2 sm:flex-nowrap">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by policy name"
              className="h-10 pl-9 pr-9 shadow-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            className="text-white"
            style={{ background: "#5B9BD5" }}
            onClick={() => openForm()}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Qardan Policy
          </Button>
        </div>
      </div>

      <Card
        className="border-[#5B9BD5]/30 bg-white"
        style={{ gap: "0px", marginTop: "10px" }}
      >
        <CardContent style={{ padding: "0px 0px 10px 0px" }}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("policyName")}
                  >
                    <div className="flex items-center gap-1">
                      Policy Name
                      {sortKey === "policyName" ? (
                        sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center gap-1">
                      Qardan Type
                      {sortKey === "type" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("employee")}
                  >
                    <div className="flex items-center gap-1">
                      Employee Type
                      {sortKey === "employee" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("employment")}
                  >
                    <div className="flex items-center gap-1">
                      Employment Status
                      {sortKey === "employment" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("grade")}
                  >
                    <div className="flex items-center gap-1">
                      Grades
                      {sortKey === "grade" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("service")}
                  >
                    <div className="flex items-center gap-1">
                      Min Service (Months)
                      {sortKey === "service" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("maximum")}
                  >
                    <div className="flex items-center gap-1">
                      Max Active Qardans
                      {sortKey === "maximum" ? (
                        sortDirection === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="cursor-pointer select-none whitespace-nowrap px-4 py-3 text-xs hover:bg-sky-100/50"
                    onClick={() => handleSort("status")}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {sortKey === "status" ? (
                        sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                      ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-right text-xs"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow key={row.id} className="hover:bg-sky-50/30">
                    <TableCell className="px-4 py-3 text-xs text-gray-700">{row.policyName ?? "—"}</TableCell>
                    <TableCell className="px-4 py-3 text-xs text-gray-700">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-100 to-blue-100">
                          <FileText className="h-4 w-4 text-[#5B9BD5]" />
                        </div>
                        <span>{row.type ?? "—"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs text-gray-700">
                      {row.employee ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs text-gray-700">
                      {row.employment ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs text-gray-700">
                      {row.grade ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs font-semibold text-[#0B5394]">
                      {row.service ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs font-semibold text-[#0B5394]">
                      {row.maximum ?? "—"}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-xs">
                      <Badge className={row.status === "Active" ? "border-green-300 bg-green-100 text-green-700" : "border-red-300 bg-red-100 text-red-700"}>
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-[#5B9BD5] hover:bg-sky-50"
                          onClick={() => openForm(row)}
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => setPolicyToDelete(row)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="px-4 py-8 text-center font-medium text-[#5B9BD5]"
                    >
                      {loadError
                        ? "Unable to load qardan policies."
                        : loading
                          ? "Loading qardan policies..."
                          : "No qardan policy found."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        <Pagination
          totalItems={totalRecords}
          itemsPerPage={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          label="qardan policies"
        />
      </Card>

      <Dialog
        open={!!editing}
        onOpenChange={(open) => !open && !saving && setEditing(null)}
      >
        <DialogContent className="!flex !max-h-[calc(100dvh-2rem)] !w-[calc(100%-2rem)] !max-w-5xl !flex-col !gap-0 overflow-hidden bg-white !p-0">
          <DialogHeader className="shrink-0 border-b px-4 py-3 pr-10 !text-left sm:px-6 sm:py-4">
            <DialogTitle style={{ color: "#5B9BD5" }}>
              {typeof form.id === "string" ? "Edit" : "Add New"}{" "}
              Qardan Policy
            </DialogTitle>
            <DialogDescription>Configure eligibility and limits for the qardan policy.</DialogDescription>
          </DialogHeader>
          <div className="grid min-h-0 flex-auto grid-cols-1 gap-3 overflow-y-auto overscroll-contain px-3 py-3 sm:grid-cols-2 sm:gap-4 sm:px-6 sm:py-5">
            {policyFields.map((field) => (
              <div
                key={field.key}
                className={
                  field.key === "policyName" ? "min-w-0 sm:col-span-2" : "min-w-0"
                }
              >
                {field.type === "select" ? (
                  <SelectFieldset label={field.label} required>
                    <Select
                      value={String(form[field.key] ?? "")}
                      onValueChange={(value) => updateField(field.key, value)}
                      disabled={optionsLoading || !!optionErrors[field.key]}
                    >
                      <SelectTriggerUI
                        aria-label={field.label}
                        aria-required="true"
                        aria-invalid={!!formErrors[field.key]}
                        aria-describedby={
                          formErrors[field.key]
                            ? `${field.key}-error`
                            : undefined
                        }
                        className={
                          formErrors[field.key] ? "border-red-500" : undefined
                        }
                      >
                        <SelectValue placeholder={optionsLoading ? "Loading..." : `Choose ${field.label}`} />
                      </SelectTriggerUI>
                      <SelectContent>
                        {getFieldOptions(field).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                ) : (
                  <InputFieldset
                    label={field.label}
                    aria-label={field.label}
                    aria-required="true"
                    aria-invalid={!!formErrors[field.key]}
                    aria-describedby={
                      formErrors[field.key] ? `${field.key}-error` : undefined
                    }
                    className={
                      formErrors[field.key] ? "border-red-500" : undefined
                    }
                    min={field.type === "number" ? 0 : undefined}
                    step={field.type === "number" ? 1 : undefined}
                    required
                    type={field.type}
                    value={String(form[field.key] ?? "")}
                    onChange={(event) =>
                      updateField(field.key, event.target.value)
                    }
                  />
                )}
                {optionErrors[field.key] && (
                  <div role="alert" className="mt-1 text-xs text-red-500">
                    {optionErrors[field.key]} <button type="button" className="underline" onClick={() => setOptionsRetry((value) => value + 1)}>Retry</button>
                  </div>
                )}
                {!optionsLoading && !optionErrors[field.key] && field.type === "select" && getFieldOptions(field).length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">No active options available.</p>
                )}
                {formErrors[field.key] && (
                  <p
                    id={`${field.key}-error`}
                    role="alert"
                    className="mt-1 text-xs text-red-500"
                  >
                    {formErrors[field.key]}
                  </p>
                )}
              </div>
            ))}
            <div className="sm:col-span-2 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
              <Label htmlFor="qardan-policy-status">Status</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{form.status}</span>
                <Switch
                  id="qardan-policy-status"
                  checked={form.status === "Active"}
                  onCheckedChange={(checked) =>
                    setForm((current) => ({
                      ...current,
                      status: checked ? "Active" : "Inactive",
                    }))
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter className="!grid shrink-0 grid-cols-2 gap-2 border-t bg-white px-3 py-3 sm:!flex sm:px-6 sm:py-4">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setEditing(null)}
              className="h-auto min-h-10 w-full min-w-0 whitespace-normal px-2 text-xs sm:w-auto sm:px-4 sm:text-sm"
            >
              Cancel
            </Button>
            <Button
              style={{ background: "#5B9BD5" }}
              className="h-auto min-h-10 w-full min-w-0 whitespace-normal px-2 text-xs text-white sm:w-auto sm:px-4 sm:text-sm"
              disabled={saving || optionsLoading}
              onClick={save}
            >
              {typeof form.id === "string" ? "Update" : "Add"} Qardan
              Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!policyToDelete}
        onOpenChange={(open) => { if (!open && !deleting) setPolicyToDelete(null); }}
        title="Delete Qardan Policy"
        description={<>Delete <strong>{String(policyToDelete?.policyName ?? "this policy")}</strong>?</>}
        confirmText="Yes, Delete"
        loading={deleting}
        onConfirm={confirmDelete}
      />

    </div>
  );
}
